<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import OrderMoneyPopover from "@/views/orders/view/components/OrderMoneyPopover.vue";
import { useCalculateOrderMoney, useOrderPayments } from "@/composables";

import type { OrderExpense, OrderPayment } from "@/api/orders/dto/order.dto";

const props = withDefaults(
  defineProps<{
    orderPayments: OrderPayment[];
    orderExpenses: OrderExpense[];
    compact?: boolean;
  }>(),
  {
    compact: false,
  },
);

const emits = defineEmits<{
  (e: "onOpenFinances"): void;
}>();

const { t } = useI18n();
const { formatOrderPaymentRate } = useOrderPayments();

const { activeOrderPayments, totalInvoiceRate, totalOrderRate } = useCalculateOrderMoney(
  () => props.orderPayments,
  () => props.orderExpenses,
  () => [],
);

const rows = computed(() => [
  {
    key: "total-invoice-rate",
    label: t("payments.total-invoice-rate"),
    hint: t("payments.total-invoice-rate-hint"),
    value: `${formatOrderPaymentRate(totalInvoiceRate.value)} (${activeOrderPayments.value.length})`,
  },
  {
    key: "total-order-rate",
    label: t("payments.total-order-rate"),
    hint: t("payments.total-order-rate-hint"),
    value: formatOrderPaymentRate(totalOrderRate.value),
  },
]);
</script>

<template>
  <v-row v-for="row in rows" :key="row.key" :class="{ 'order-rates--compact': props.compact }" class="order-rates">
    <div class="order-rates__label d-flex align-center">
      <span class="text-grey-darken-1">{{ row.label }}</span>
      <omni-tooltip>
        <template #activator>
          <v-icon class="ml-1" color="grey-darken-1" icon="mdi-information-outline" size="16" />
        </template>
        {{ row.hint }}
      </omni-tooltip>
    </div>

    <div :data-qa="row.key" class="order-rates__value">
      <order-money-popover
        :order-expenses="orderExpenses"
        :order-payments="activeOrderPayments"
        @on-open-finances="emits('onOpenFinances')"
      >
        <template #activator="{ isActive }">
          <span class="d-inline-flex align-center order-rates__activator">
            {{ row.value }}
            <v-icon :icon="isActive ? 'mdi-chevron-up' : 'mdi-chevron-down'" size="20" />
          </span>
        </template>
      </order-money-popover>
    </div>
  </v-row>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.order-rates__label {
  padding: 12px;
  max-width: 190px;
  flex: 0 0 190px;
}

.order-rates__value {
  color: $grey-navy;
  padding: 12px;
  flex: 1;
}

.order-rates__activator {
  gap: 4px;
}

.order-rates--compact {
  margin: 0;
  align-items: center;
  column-gap: 32px;

  & + & {
    margin-top: 12px;
  }

  .order-rates__label {
    padding: 0;
    max-width: 134px;
    flex-basis: 134px;
    white-space: nowrap;
  }

  .order-rates__value {
    padding: 0;
    flex: 0 1 auto;
    white-space: nowrap;
  }
}
</style>
